import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { seoRoutes, defaultSEO } from '../config/seoConfig';

/**
 * SEO Component using react-helmet-async
 * 
 * Dynamically sets page title and meta description based on current route.
 * Can accept props for backward compatibility with old inline SEO usage.
 * Falls back to seoRoutes config, then to defaultSEO.
 * 
 * Usage: 
 * - Place once at Router level for automatic path-based SEO
 * - Or use with props: <SEO title="..." description="..." />
 */
const SEO = ({ title: propTitle, description: propDescription, keywords } = {}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Priority: Props > seoRoutes > defaultSEO
  const seoData = propTitle && propDescription 
    ? { title: propTitle, description: propDescription }
    : (seoRoutes[currentPath] || defaultSEO);
  
  return (
    <Helmet>
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://raven-environmental.com${currentPath}`} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={`https://raven-environmental.com${currentPath}`} />
    </Helmet>
  );
};

export default SEO;
